/**
 * Utility functions for formatting provider data
 */

/**
 * Format service range enum values for display
 */
export const formatServiceRange = (range: string): string => {
  const rangeMap: Record<string, string> = {
    IN_HOME: 'In-Home',
    VIRTUAL: 'Virtual',
    IN_OFFICE: 'In-Office',
  };
  return rangeMap[range] || range;
};

/**
 * Partner company info for display name resolution
 */
type PartnerCompanyInfo = {
  name: string;
} | null | undefined;

/**
 * User info for display name resolution
 */
type UserInfo = {
  firstName: string;
  lastName?: string | null;
} | null | undefined;

/**
 * Get the display name for a provider using the fallback chain (FR-007e):
 * 1. partnerCompany.name (if assigned to a partner company)
 * 2. companyName (existing field)
 * 3. firstName + lastName (user's full name)
 * 
 * @param partnerCompany - The partner company object (may be null)
 * @param companyName - The company name field
 * @param user - The user object with firstName and lastName
 * @returns The display name based on the fallback chain
 */
export const getProviderDisplayName = (
  partnerCompany: PartnerCompanyInfo,
  companyName: string | null | undefined,
  user: UserInfo
): string => {
  // Priority 1: Partner company name
  if (partnerCompany?.name) {
    return partnerCompany.name;
  }

  // Priority 2: Company name field
  if (companyName) {
    return companyName;
  }

  // Priority 3: User's full name
  if (user?.firstName) {
    const lastName = user.lastName || '';
    return `${user.firstName}${lastName ? ` ${lastName}` : ''}`.trim();
  }

  // Fallback: Unknown Provider
  return 'Unknown Provider';
};

/**
 * Get the first character for avatar/placeholder based on display name
 * Uses the same fallback chain as getProviderDisplayName
 * 
 * @param partnerCompany - The partner company object (may be null)
 * @param companyName - The company name field
 * @param user - The user object with firstName and lastName
 * @returns The first character for display
 */
export const getProviderDisplayInitial = (
  partnerCompany: PartnerCompanyInfo,
  companyName: string | null | undefined,
  user: UserInfo
): string => {
  const displayName = getProviderDisplayName(partnerCompany, companyName, user);
  return displayName.charAt(0).toUpperCase() || '?';
};

